import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { findAnswer } from "../../chat/match";
import { SUGGESTED } from "../../chat/knowledge";
import { logQuestion } from "../../chat/logQuestion";
import "./help-widget.css";

const WHATSAPP = "https://api.whatsapp.com/send?phone=916264208349";

/**
 * The on-site help widget.
 *
 * Answers come from a fixed knowledge base via deterministic matching — there
 * is no model and no network call, so the widget cannot invent a price or a
 * policy. When it isn't confident it says so and offers WhatsApp, which is the
 * same place the visitor would have gone anyway.
 *
 * It runs entirely in the browser and costs nothing to operate. Its other job
 * is to record the questions people ask, which is the dataset Phase 2 needs.
 */
export default function HelpWidget() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [turns, setTurns] = useState([]);
  const endRef = useRef(null);
  const inputRef = useRef(null);

  // Checkout is not the place for a help panel to dock over the form.
  const hidden = ["/cart", "/confirmation", "/dashboard"].includes(pathname);

  useEffect(() => {
    if (open) endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [turns, open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const ask = useCallback(
    (raw) => {
      const question = String(raw || "").trim();
      if (!question) return;

      const result = findAnswer(question);
      setTurns((t) => [...t, { role: "user", text: question }, { role: "bot", result }]);
      setInput("");

      logQuestion({
        question,
        matchedId: result.entry ? result.entry.id : null,
        score: result.score,
        path: pathname,
      });
    },
    [pathname]
  );

  if (hidden) return null;

  return (
    <>
      <button
        className={`nt-help-fab ${open ? "is-open" : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls="nt-help-panel"
        aria-label={open ? "Close help" : "Ask a question or chat with us"}
      >
        {open ? <i className="fas fa-times" aria-hidden="true" /> : <i className="fas fa-comment-dots" aria-hidden="true" />}
        {!open && <span className="nt-help-fab-text">Ask us</span>}
      </button>

      {open && (
        <div className="nt-help-panel" id="nt-help-panel" role="dialog" aria-label="Help">
          <header className="nt-help-head">
            <div>
              <strong>Ask NT Sticks</strong>
              <span>Answers about products, delivery and wholesale</span>
            </div>
            <button onClick={() => setOpen(false)} aria-label="Close">
              <i className="fas fa-times" aria-hidden="true" />
            </button>
          </header>

          <a
            className="nt-help-wa"
            href={WHATSAPP}
            target="_blank"
            rel="noopener noreferrer"
          >
            <i className="fab fa-whatsapp" aria-hidden="true" />
            <span>
              <strong>Chat with us on WhatsApp</strong>
              <em>Typically replies within 30 mins</em>
            </span>
            <i className="fas fa-chevron-right nt-help-wa-go" aria-hidden="true" />
          </a>

          <div className="nt-help-body">
            {turns.length === 0 && (
              <div className="nt-help-intro">
                <p>Hello. Ask a question and I'll answer from our product and policy pages.</p>
                <div className="nt-help-chips">
                  {SUGGESTED.map((q) => (
                    <button key={q} onClick={() => ask(q)}>{q}</button>
                  ))}
                </div>
              </div>
            )}

            {turns.map((turn, i) =>
              turn.role === "user" ? (
                <p className="nt-help-q" key={i}>{turn.text}</p>
              ) : (
                <Answer key={i} result={turn.result} onAsk={ask} onClose={() => setOpen(false)} />
              )
            )}
            <div ref={endRef} />
          </div>

          <form
            className="nt-help-form"
            onSubmit={(e) => {
              e.preventDefault();
              ask(input);
            }}
          >
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your question…"
              maxLength={300}
              aria-label="Your question"
            />
            <button type="submit" disabled={!input.trim()} aria-label="Send">
              <i className="fas fa-paper-plane" aria-hidden="true" />
            </button>
          </form>

          <p className="nt-help-foot">
            Answers come from our product and policy pages — no guessing.
          </p>
        </div>
      )}
    </>
  );
}

function Answer({ result, onAsk, onClose }) {
  // The unconfident branch deliberately offers a person rather than a guess.
  if (!result.confident) {
    return (
      <div className="nt-help-a nt-help-a-miss">
        <p>I don't have a reliable answer for that one, and I'd rather not guess.</p>
        <div className="nt-help-actions">
          <a className="nt-help-btn primary" href={WHATSAPP} target="_blank" rel="noopener noreferrer">
            Ask on WhatsApp
          </a>
          <Link className="nt-help-btn" to="/contact" onClick={onClose}>Contact us</Link>
        </div>
      </div>
    );
  }

  const { entry, alternatives } = result;
  return (
    <div className="nt-help-a">
      <p>{entry.answer}</p>
      {entry.links?.length > 0 && (
        <div className="nt-help-actions">
          {entry.links.map((l) =>
            l.href ? (
              <a className="nt-help-btn" key={l.label} href={l.href} target="_blank" rel="noopener noreferrer">
                {l.label}
              </a>
            ) : (
              <Link className="nt-help-btn" key={l.label} to={l.to} onClick={onClose}>
                {l.label}
              </Link>
            )
          )}
        </div>
      )}
      {alternatives.length > 0 && (
        <p className="nt-help-alt">
          Did you mean:{" "}
          {alternatives.map((a) => (
            <button key={a.id} onClick={() => onAsk(a.asks[0])}>{a.title}</button>
          ))}
        </p>
      )}
    </div>
  );
}
